import React from 'react';
import { DEBUG_ENABLED } from '../../../utils/DebugLogger';
import { Group, Line, Rect } from 'react-konva';
import PropTypes from 'prop-types';

/**
 * Grid Component - Battle Map Grid Overlay System
 * 
 * This component draws the grid lines on the battle map to help with movement
 * and positioning during D&D combat. It's like the grid paper underneath
 * miniatures on a physical gaming table.
 * 
 * WHAT IT DOES:
 * - Draws horizontal and vertical grid lines across the entire map
 * - Provides visual reference for movement distances (each square = 5 feet in D&D)
 * - Supports different grid sizes (20px to 100px squares)
 * - Can be toggled on/off for maps that don't need grids
 * - Automatically adjusts to zoom level changes
 * 
 * CRITICAL LAYERING:
 * This component MUST be rendered in the correct order for proper display:
 * 1. MapArea (background maps) - bottom layer
 * 2. Grid (this component) - middle layer, visible on top of maps
 * 3. Tokens (characters/monsters) - top layer, visible on top of grid
 * 
 * GRID FEATURES:
 * - Square grid system (most common for D&D)
 * - Configurable size: 25px, 50px, 75px, 100px squares
 * - Semi-transparent white lines (visible on dark maps)
 * - Responsive to zoom: grid stays proportional when zooming in/out
 * - Performance optimized: only redraws when necessary
 */
const Grid = ({ 
  visible = true, 
  gridSize = 50, 
  gridOpacity = 0.5, 
  gridWidth = 40, 
  gridHeight = 30, 
  stageWidth, 
  stageHeight, 
  mapWidth,
  mapHeight,
  color = '#ffffff', 
  opacity = 0.8, 
  gridType = 'square' 
}) => {
  // Don't render anything if grid is disabled or size is invalid
  if (!visible || !gridSize) {
    return null;
  }
  
  // Ensure gridWidth and gridHeight are always valid numbers
  const safeGridWidth = Math.max(parseInt(gridWidth) || 40, 1);
  const safeGridHeight = Math.max(parseInt(gridHeight) || 30, 1);
  const safeMapWidth = Math.max(Number(mapWidth) || 0, 0);
  const safeMapHeight = Math.max(Number(mapHeight) || 0, 0);
  const renderWidth = Math.max(Number(stageWidth) || 0, safeMapWidth, safeGridWidth * gridSize);
  const renderHeight = Math.max(Number(stageHeight) || 0, safeMapHeight, safeGridHeight * gridSize);
  const effectiveGridWidth = Math.max(safeGridWidth, Math.ceil(safeMapWidth / gridSize) || 0);
  const effectiveGridHeight = Math.max(safeGridHeight, Math.ceil(safeMapHeight / gridSize) || 0);

  // Grid should cover the full map footprint even when restored room settings
  // contain stale column/row counts that would otherwise collapse the overlay.
  const gridBoundsWidth = effectiveGridWidth * gridSize;
  const gridBoundsHeight = effectiveGridHeight * gridSize;
  
  if (DEBUG_ENABLED) console.debug(`[Grid] Rendering with dimensions: ${effectiveGridWidth}x${effectiveGridHeight} cells (${gridBoundsWidth}x${gridBoundsHeight}px) at ${gridSize}px cell size`); // date_of_change: 2025-08-22

  const lines = [];

  // Handle different grid types
  if (gridType === 'square') {
    // Vertical lines - only within grid bounds
    for (let i = 0; i <= effectiveGridWidth; i++) {
      lines.push(
        <Line
          key={`v-${i}`}
          points={[Math.round(i * gridSize), 0, Math.round(i * gridSize), gridBoundsHeight]}
          stroke={color}
          strokeWidth={1}
          opacity={gridOpacity}
          perfectDrawEnabled={false}
          listening={false}
        />
      );
    }

    // Horizontal lines - only within grid bounds
    for (let i = 0; i <= effectiveGridHeight; i++) {
      lines.push(
        <Line
          key={`h-${i}`}
          points={[0, Math.round(i * gridSize), gridBoundsWidth, Math.round(i * gridSize)]}
          stroke={color}
          strokeWidth={1}
          opacity={gridOpacity}
          perfectDrawEnabled={false}
          listening={false}
        />
      );
    }
  } else if (gridType === 'hex-flat' || gridType === 'hex') {
    // Hexagonal grid with flat-topped hexagons
    const hexWidth = gridSize;
    const hexHeight = gridSize * Math.sqrt(3) / 2;
    const vertSpacing = hexHeight * 3 / 4;

    for (let row = 0; row * vertSpacing <= renderHeight + hexHeight; row++) {
      for (let col = 0; col * hexWidth <= renderWidth + hexWidth; col++) {
        const x = col * hexWidth + (row % 2) * (hexWidth / 2);
        const y = row * vertSpacing;

        if (x <= renderWidth && y <= renderHeight) {
          // Draw hexagon outline
          const points = [];
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            const px = x + (hexWidth / 2) * Math.cos(angle);
            const py = y + (hexHeight / 2) * Math.sin(angle);
            points.push(px, py);
          }
          points.push(points[0], points[1]); // Close the hexagon

          lines.push(
            <Line
              key={`hex-${row}-${col}`}
              points={points}
              stroke={color}
              strokeWidth={1}
              opacity={opacity}
              listening={false}
            />
          );
        }
      }
    }
  } else if (gridType === 'hex-point') {
    // Hexagonal grid with pointy-topped hexagons
    const hexWidth = gridSize * Math.sqrt(3) / 2;
    const hexHeight = gridSize;
    const horizSpacing = hexWidth * 3 / 4;

    for (let col = 0; col * horizSpacing <= renderWidth + hexWidth; col++) {
      for (let row = 0; row * hexHeight <= renderHeight + hexHeight; row++) {
        const x = col * horizSpacing;
        const y = row * hexHeight + (col % 2) * (hexHeight / 2);

        if (x <= renderWidth && y <= renderHeight) {
          // Draw hexagon outline
          const points = [];
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3 + Math.PI / 6; // Rotate by 30 degrees
            const px = x + (hexWidth / 2) * Math.cos(angle);
            const py = y + (hexHeight / 2) * Math.sin(angle);
            points.push(px, py);
          }
          points.push(points[0], points[1]); // Close the hexagon

          lines.push(
            <Line
              key={`hex-${row}-${col}`}
              points={points}
              stroke={color}
              strokeWidth={1}
              opacity={opacity}
              listening={false}
            />
          );
        }
      }
    }
  }

  // Return lines in Group with explicit layering to ensure they render above map
  return (
    <Group listening={false} opacity={1.0}>
      {/* Transparent background to ensure proper layer stacking - bounded by grid dimensions */}
      <Rect
        x={0}
        y={0}
        width={gridBoundsWidth}
        height={gridBoundsHeight}
        fill="transparent"
        listening={false}
      />
      {/* Add a semi-transparent overlay for grid visibility */}
      <Rect
        x={0}
        y={0}
        width={gridBoundsWidth}
        height={gridBoundsHeight}
        fill="rgba(0, 0, 0, 0.05)"
        listening={false}
      />
      {lines}
    </Group>
  );
};

Grid.propTypes = {
  visible: PropTypes.bool,
  gridSize: PropTypes.number,
  gridOpacity: PropTypes.number,
  gridWidth: PropTypes.number,
  gridHeight: PropTypes.number,
  stageWidth: PropTypes.number,
  stageHeight: PropTypes.number,
  mapWidth: PropTypes.number,
  mapHeight: PropTypes.number,
  color: PropTypes.string,
  opacity: PropTypes.number,
  gridType: PropTypes.oneOf(['square', 'hex', 'hex-flat', 'hex-pointy', 'none'])
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Check critical props that would cause visual changes
  if (prevProps.visible !== nextProps.visible) return false;
  if (prevProps.gridSize !== nextProps.gridSize) return false;
  if (prevProps.gridType !== nextProps.gridType) return false;
  if (prevProps.gridOpacity !== nextProps.gridOpacity) return false;
  
  // Check stage dimensions - important for grid calculation
  if (Math.abs((prevProps.stageWidth || 0) - (nextProps.stageWidth || 0)) > 1) return false;
  if (Math.abs((prevProps.stageHeight || 0) - (nextProps.stageHeight || 0)) > 1) return false;
  if (Math.abs((prevProps.mapWidth || 0) - (nextProps.mapWidth || 0)) > 1) return false;
  if (Math.abs((prevProps.mapHeight || 0) - (nextProps.mapHeight || 0)) > 1) return false;
  
  // Grid bounds changes - ensure we're comparing numbers
  // FIXED: Ensure proper comparison of grid dimensions by parsing to integers - date_of_change: 2025-08-22
  const prevWidth = parseInt(prevProps.gridWidth) || 40;
  const nextWidth = parseInt(nextProps.gridWidth) || 40;
  const prevHeight = parseInt(prevProps.gridHeight) || 30;
  const nextHeight = parseInt(nextProps.gridHeight) || 30;
  
  if (prevWidth !== nextWidth) {
    if (DEBUG_ENABLED) console.debug(`[Grid] Re-rendering due to gridWidth change: ${prevWidth} -> ${nextWidth}`);
    return false;
  }
  
  if (prevHeight !== nextHeight) {
    if (DEBUG_ENABLED) console.debug(`[Grid] Re-rendering due to gridHeight change: ${prevHeight} -> ${nextHeight}`);
    return false;
  }
  
  // Appearance properties
  if (prevProps.color !== nextProps.color) return false;
  if (Math.abs(prevProps.opacity - nextProps.opacity) > 0.01) return false;
  
  // If we got here, the props that matter haven't changed
  if (DEBUG_ENABLED) console.debug('[Grid] Skipping re-render - props are equivalent');
  return true;
};

export default React.memo(Grid, arePropsEqual);
